import { useApiClient } from '@startupquickstart/frontend';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

interface GenerateTranslationsParams {
  attractionId: string;
  themeId: string;
  targetLanguageIds: string[];
}

interface GenerateTranslationsResponse {
  success: boolean;
  message?: string;
  error?: string;
  data?: {
    created: string[];
    skipped: Array<{
      languageId: string;
      reason: string;
    }>;
    sourceTheme: {
      id: string;
      language: {
        id: string;
        name: string;
        code: string;
      };
    };
  };
}

/**
 * Hook for generating theme translations to other languages
 */
export function useGenerateTranslations() {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      attractionId,
      themeId,
      targetLanguageIds,
    }: GenerateTranslationsParams) => {
      const response = (await apiClient.post(
        `/attractions/${attractionId}/themes/${themeId}/generate-translations`,
        { targetLanguageIds },
      )) as GenerateTranslationsResponse;

      if (!response || response.success === false) {
        throw new Error(response?.error || 'No response data');
      }
      return response;
    },
    onSuccess: (data, variables) => {
      if (data.success && data.data) {
        // Invalidate attraction themes queries to refresh the list
        queryClient.invalidateQueries({ queryKey: ['AttractionTheme', variables.attractionId] });
        queryClient.invalidateQueries({ queryKey: ['AttractionTheme'] });

        const createdCount = data.data.created.length;
        const skippedCount = data.data.skipped.length;

        if (createdCount > 0) {
          toast.success(
            `Created ${createdCount} translation${createdCount > 1 ? 's' : ''}${
              skippedCount > 0 ? `. Skipped ${skippedCount} (already exist)` : ''
            }`,
          );
        } else if (skippedCount > 0) {
          toast.info(`All selected languages already have themes`);
        }
      }
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to generate translations');
    },
  });
}
